import features from '@/lib/features'

import { STORAGE_KEY } from './constants'

/**
 * @returns {boolean}
 */
function isLocalStorageAvailable() {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const storage = window.localStorage
    const testKey = '__i18n_storage_test__'
    storage.setItem(testKey, testKey)
    storage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

/**
 * @returns {string}
 */
export function getDefaultLocale() {
  return features?.i18n?.config?.defaultLocale || 'de'
}

/**
 * @param {string} locale
 * @param {string[]} availableLocales
 * @returns {string|string[]}
 */
export function getFallbackLocale(locale, availableLocales) {
  const defaultLocale = getDefaultLocale()

  if (locale.includes('-')) {
    const baseLanguage = locale.split('-')[0]

    if (availableLocales.includes(baseLanguage)) {
      return baseLanguage !== defaultLocale ? [baseLanguage, defaultLocale] : [baseLanguage]
    }
  }

  return defaultLocale
}

/**
 * @returns {string|null}
 */
export function getLocaleFromStorage() {
  if (!isLocalStorageAvailable()) {
    return null
  }

  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * @param {string} locale
 */
export function saveLocaleToStorage(locale) {
  if (!isLocalStorageAvailable()) {
    return
  }

  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Ignore storage failures (private mode, quota, disabled storage).
  }
}

/**
 * @returns {string[]}
 */
function getBrowserLanguages() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return []
  }

  if (navigator.languages?.length) {
    return [...navigator.languages]
  }

  return navigator.language ? [navigator.language] : []
}

/**
 * @param {string[]} availableLocales
 * @returns {string|null}
 */
export function getBrowserLocale(availableLocales) {
  for (const browserLocale of getBrowserLanguages()) {
    if (availableLocales.includes(browserLocale)) {
      return browserLocale
    }

    const language = browserLocale.split('-')[0]
    if (availableLocales.includes(language)) {
      return language
    }
  }
  return null
}

/**
 * @param {string[]} availableLocales
 * @returns {string}
 */
export function getClientLocale(availableLocales) {
  const selectedLocale = getLocaleFromStorage()
  if (selectedLocale && availableLocales.includes(selectedLocale)) {
    return selectedLocale
  }

  const browserLocale = getBrowserLocale(availableLocales)
  if (browserLocale) {
    return browserLocale
  }

  return getDefaultLocale()
}
